// Libs
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QrReader from 'modern-react-qr-reader';
import axios from 'axios';
// Components
import { useComponentsPool } from '../../../../ComponentsPool';

/*
 * How the rear camera is selected
 *
 * Qr reader library picks the camera itself on Android/Chromium (not on iOS or
 * Firefox, where it only uses `constraints`):
 *   1. it keeps the cameras whose label matches /rear|back|environment/ AND contains `cameraId`
 *      (default 'camera2 0'), and takes the first one;
 *   2. otherwise it takes the 2nd camera of the list (`videoDevices[1]`).
 * The chosen deviceId is then merged with `constraints` and sent to getUserMedia.
 *
 * Chrome on Android labels its cameras "camera2 <index>, facing <front|back>" before Chrome 139 and
 * "camera <index>, facing <front|back>" since Chrome 139 (Aug. 2025). It lists every front camera first, then
 * the rear cameras in reverse index order. Since Chrome 139 the default 'camera2 0' never matches, so the lib
 * falls back to the 2nd camera, which is a front one on phones exposing two front cameras: the selfie camera
 * opens. `facingMode: { ideal: 'environment' }` does not help: the bare deviceId is also an "ideal" constraint,
 * the front and rear cameras get the same score and Chrome breaks the tie with the list order (front first).
 *
 * So the scanner:
 *   - targets the main rear camera with MAIN_REAR_CAMERA_LABEL, which matches both label formats. On Android,
 *     camera index 0 is the main rear camera by convention, and ' 0, facing back' does not match "camera 10";
 *   - first requires a rear camera (`exact`), so a selfie camera can never be opened when a rear one exists,
 *     even if Chrome changes its labels again;
 *   - retries once with `ideal` if the first attempt fails (no rear camera: computer, DevTools mobile emulation,
 *     some tablets). The QrReader is remounted with a new `key` because the lib does not restart the camera
 *     when `constraints` change.
 *
 * Known limit: browsers hide camera labels until the camera permission is granted. On the very first opening
 * (or on every opening with an "only this time" permission) MAIN_REAR_CAMERA_LABEL cannot match, and Chrome may
 * open a secondary rear lens (ultra-wide, macro), never the selfie one.
 *
 * When the browser lists no camera at all, the lib fails with NoVideoInputDevicesError without ever calling
 * getUserMedia, so nothing asks for the camera permission. Some browsers hide their cameras until this permission is
 * granted: the scanner then opens a stream itself (which asks for it), closes it, waits CAMERA_RELEASE_DELAY_MS
 * for Android to release the camera and restarts the lib, once per opening. If the cameras are still missing,
 * retrying with `ideal` would list them the same way: the error is displayed. Note that Chromium before 128
 * (e.g. Samsung Internet 24) counts the cameras once per browser process: a single Android failure at that moment
 * makes it list no camera until the browser is restarted, no code can fix it.
 *
 * Samsung Internet (seen on 24, Chromium 117) can leave the <video> blank while the camera stream is live: the
 * frames are decoded, but the video layer is never painted. On this browser the scanner hides the video and draws
 * its frames on a canvas placed over it. The lib keeps decoding from the hidden video.
 *
 * Every step is logged in the console with LOG_PREFIX. Only real bugs are sent to the backend.
 *   - a front camera was opened;
 *   - every attempt failed and the error message is displayed, unless the user refused the camera permission
 *     (NotAllowedError), which is not a bug.
 * An attempt that fails but is recovered by the next one is only logged in the console.
 */
const MAIN_REAR_CAMERA_LABEL = ' 0, facing back';
const CAMERA_ATTEMPTS = [
    { name: 'exact', constraints: { facingMode: { exact: 'environment' } } },
    { name: 'ideal', constraints: { facingMode: { ideal: 'environment' } } }
];
const LOG_PREFIX = '[QRCodeScanner]';
const NO_LABEL = '(no label)';
const PERMISSION_DENIED_ERROR = 'NotAllowedError';
const NO_CAMERA_FOUND_ERROR = 'NoVideoInputDevicesError';
const CAMERA_ACCESS_CONSTRAINTS = { facingMode: { ideal: 'environment' } };
// Android releases a stopped camera asynchronously: reopening it right away can fail with NotReadableError
const CAMERA_RELEASE_DELAY_MS = 1000;
const IS_SAMSUNG_INTERNET = /SamsungBrowser/i.test(navigator.userAgent);

/* eslint-disable no-console */
const log = (...args) => console.log(LOG_PREFIX, ...args);
const logWarning = (...args) => console.warn(LOG_PREFIX, ...args);
const logError = (...args) => console.error(LOG_PREFIX, ...args);
/* eslint-enable no-console */

const getVideoInputLabels = async () => {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter(device => device.kind === 'videoinput').map(device => device.label || NO_LABEL);
    } catch (error) {
        return [`enumerateDevices failed: ${error?.name}: ${error?.message}`];
    }
};

// The phone model is no longer in Chrome's user agent, it is only exposed through the User-Agent Client Hints
const getDeviceModel = async () => {
    try {
        const { model, platformVersion } = await navigator.userAgentData.getHighEntropyValues([
            'model',
            'platformVersion'
        ]);
        return { model, platformVersion };
    } catch (error) {
        return null;
    }
};

// Asks for the camera access by opening a stream, then releases the camera and waits before the lib reopens it
const requestCameraAccess = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: CAMERA_ACCESS_CONSTRAINTS });
    stream.getTracks().forEach(track => track.stop());
    await new Promise(resolve => setTimeout(resolve, CAMERA_RELEASE_DELAY_MS));
};

// Same payload as the ErrorBoundary: the API tracks it on Segment, which forwards it to Slack
const reportScannerBug = async (summary, details) => {
    try {
        const payload = {
            external_reference: 'merciyanis_app_qrcode_scanner',
            url: window.location.href,
            error: `${LOG_PREFIX} ${summary}`,
            message: JSON.stringify(
                {
                    ...details,
                    cameraId: MAIN_REAR_CAMERA_LABEL,
                    cameras: await getVideoInputLabels(),
                    device: await getDeviceModel(),
                    // Names the browser explicitly (e.g. "Samsung Internet"), Chromium browsers only
                    browserBrands: navigator.userAgentData?.brands || null,
                    userAgent: navigator.userAgent
                },
                null,
                2
            ),
            workspace: localStorage?.getItem('iot_rocket_workspace') || 'Unknown workspace'
        };
        await axios.post('errors/', payload);
    } catch (error) {
        logWarning(`Could not report "${summary}"`, error);
    }
};

const MobileQRCodeScanner = () => {
    const { Component } = useComponentsPool();
    const [t] = useTranslation();
    const [isError, setIsError] = useState(false);
    const [attemptIndex, setAttemptIndex] = useState(0);
    const attempt = CAMERA_ATTEMPTS[attemptIndex];
    const [hasRequestedCameraAccess, setHasRequestedCameraAccess] = useState(false);
    // Failures of the previous attempts and result of the camera access request, sent along with a bug report
    const failedAttempts = useRef([]);
    const cameraAccessRequest = useRef(null);
    const videoContainer = useRef(null);
    const previewCanvas = useRef(null);
    const previewFrame = useRef(null);

    useEffect(() => () => cancelAnimationFrame(previewFrame.current), []);

    useEffect(() => {
        getVideoInputLabels().then(labels => {
            log('Cameras seen by the browser:', labels);
            if (labels.every(label => label === NO_LABEL)) {
                log('Camera labels are hidden until the camera permission is granted: the main rear camera cannot be targeted on this opening');
            }
        });
    }, []);

    useEffect(() => {
        log(`Opening the camera, attempt "${attempt.name}"`, {
            cameraId: MAIN_REAR_CAMERA_LABEL,
            constraints: attempt.constraints
        });
    }, [attempt]);

    // We scan every 100 ms (delay), once a QR Code was scanned we redirect the user to it's link
    const handleScan = data => {
        if (data) {
            //window.location.href = data;
            const url = new URL(data); // data = URL complète
            const path = url.pathname + url.search + url.hash;
            log('QR code scanned, redirecting to', path);
            window.history.pushState({}, '', path);
            window.location.reload();
        }
    };

    // Draws every video frame on the preview canvas, cropped like `object-fit: cover`, until the canvas is removed
    const startCanvasPreview = () => {
        const video = videoContainer.current?.querySelector('video');
        const canvas = previewCanvas.current;
        if (!video || !canvas) {
            logWarning('Canvas preview not started: video or canvas missing');
            return;
        }
        log('Samsung Internet: the camera is displayed through a canvas');
        cancelAnimationFrame(previewFrame.current);
        const context = canvas.getContext('2d');
        const draw = () => {
            if (!canvas.isConnected) {
                return;
            }
            const width = Math.round(canvas.clientWidth * window.devicePixelRatio);
            const height = Math.round(canvas.clientHeight * window.devicePixelRatio);
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
            }
            const { videoWidth, videoHeight } = video;
            if (videoWidth && videoHeight && width && height) {
                const scale = Math.max(width / videoWidth, height / videoHeight);
                const sourceWidth = width / scale;
                const sourceHeight = height / scale;
                context.drawImage(
                    video,
                    (videoWidth - sourceWidth) / 2,
                    (videoHeight - sourceHeight) / 2,
                    sourceWidth,
                    sourceHeight,
                    0,
                    0,
                    width,
                    height
                );
            }
            previewFrame.current = requestAnimationFrame(draw);
        };
        draw();
    };

    // The lib calls onLoad without argument in legacy mode (not used here)
    const handleLoad = ({ streamLabel = '' } = {}) => {
        const isMainRearCamera = streamLabel.includes(MAIN_REAR_CAMERA_LABEL);
        log(`Camera opened: "${streamLabel}"`, { attempt: attempt.name, isAndroidMainRearCamera: isMainRearCamera });
        if (IS_SAMSUNG_INTERNET) {
            startCanvasPreview();
        }
        // "facing front" on Chrome Android, "Front Camera" / "Caméra avant" on Safari
        if (/front|avant/i.test(streamLabel)) {
            logError('A front camera was opened instead of the rear one');
            reportScannerBug('Front camera opened instead of the rear one', {
                attempt: attempt.name,
                constraints: attempt.constraints,
                streamLabel,
                failedAttempts: failedAttempts.current,
                cameraAccessRequest: cameraAccessRequest.current
            });
        } else if (attemptIndex > 0) {
            log(`Fallback attempt "${attempt.name}" succeeded`);
        }
    };

    const failScanner = error => {
        setIsError(error);
        if (error?.name === PERMISSION_DENIED_ERROR) {
            log('The camera permission was refused by the user: not reported');
        } else {
            reportScannerBug('Camera unavailable: every attempt failed', {
                failedAttempts: failedAttempts.current,
                cameraAccessRequest: cameraAccessRequest.current
            });
        }
    };

    // The lib listed no camera, so it never asked for the camera access: ask for it, then restart the lib
    const handleNoCameraFound = error => {
        if (hasRequestedCameraAccess) {
            log('Still no camera listed after the camera access was granted');
            failScanner(error);
            return;
        }
        log('No camera listed by the browser: requesting the camera access');
        requestCameraAccess()
            .then(() => {
                log('Camera access granted: restarting the scanner');
                cameraAccessRequest.current = { status: 'granted' };
                setHasRequestedCameraAccess(true);
            })
            .catch(accessError => {
                logError(`Camera access request failed: ${accessError?.name}: ${accessError?.message}`, accessError);
                cameraAccessRequest.current = {
                    status: 'failed',
                    errorName: accessError?.name,
                    errorMessage: accessError?.message
                };
                failScanner(accessError);
            });
    };

    const handleError = error => {
        const nextAttempt = CAMERA_ATTEMPTS[attemptIndex + 1];
        logError(`Attempt "${attempt.name}" failed: ${error?.name}: ${error?.message}`, error);
        failedAttempts.current.push({
            attempt: attempt.name,
            constraints: attempt.constraints,
            errorName: error?.name,
            errorMessage: error?.message,
            // Set on OverconstrainedError: the constraint no camera could satisfy
            failedConstraint: error?.constraint
        });
        if (error?.name === NO_CAMERA_FOUND_ERROR) {
            handleNoCameraFound(error);
            return;
        }
        if (nextAttempt) {
            log(`Retrying with attempt "${nextAttempt.name}"`);
            setAttemptIndex(attemptIndex + 1);
            return;
        }
        failScanner(error);
    };

    const Form = (
        <div className={'qrcode-scanner-container'}>
            <div className={'qrcode-scanner-header'}>
                <span style={{ fontWeight: 'bold' }}>{t('rounds:scan_the_qrcode')}</span>
                <span>{t('rounds:scanner_qrocde_explanation')}</span>
            </div>
            {
                !isError ? (
                    <div
                        ref={videoContainer}
                        className={`qrcode-video-container${IS_SAMSUNG_INTERNET ? ' canvas-preview' : ''}`}
                    >
                        <QrReader
                            key={`${attempt.name}-${hasRequestedCameraAccess}`}
                            delay={200}
                            facingMode={'environment'}
                            cameraId={MAIN_REAR_CAMERA_LABEL}
                            constraints={attempt.constraints}
                            onError={handleError}
                            onLoad={handleLoad}
                            onScan={handleScan}
                            style={{ width: '100%', height: '100%' }}
                        />
                        {IS_SAMSUNG_INTERNET && <canvas ref={previewCanvas} className={'qrcode-canvas-preview'} />}
                    </div>
                ) : <p className={'qrcode-error-text'}>{t('rounds:error_scanner')}</p>
            }
            <div className={'qrcode-icon'}>
                <Component componentName={'Icon'} type={'QRCodeScan'} />
            </div>
        </div>
    );

    return (
        <Component
            componentName={'Modal'}
            customModalRedirection={'MobileRoundsInstanceDetails'}
            noOverflow
            content={Form}
            classNames={'qrcode-scanner-popup-mobile'}
        />
    );
};
export default MobileQRCodeScanner;
